import React, { useCallback, useEffect, useState } from 'react';
import { Zap, TrendingUp, TrendingDown, Clock, Flame, ShieldCheck, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * VelocityHeartbeat — 24h rolling KPI ribbon for the Secure Vault Overview.
 *
 *   ⚡ VELOCITY HEARTBEAT (24H ROLLING)
 *   Inbound Enquiries · Avg Response Time · Hot Handoff Rate · Postmark Status
 *
 * Polls /api/vault/velocity-heartbeat every 30s. Each tile shows its own
 * trend pill / qualifier so the founder sees telemetry, not bare numbers.
 */

const PALETTE = {
  bgFrom: '#0F0A1A',
  bgTo: '#1F1532',
  accent: '#F99B7D',
  mint: '#A3E2C9',
  lavender: '#9BB8ED',
  amber: '#F5C84B',
  cream: '#FAF4E6',
  muted: 'rgba(250, 244, 230, 0.62)',
};

const VelocityHeartbeat = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.request('/api/vault/velocity-heartbeat');
      if (res?.inbound !== undefined) {
        setData(res);
        setPulse(true);
        setTimeout(() => setPulse(false), 600);
      }
    } catch {
      // swallow — ribbon hides gracefully if endpoint fails
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  if (loading) {
    return (
      <div
        className="rounded-2xl p-4 flex items-center gap-3"
        style={{ background: `linear-gradient(90deg, ${PALETTE.bgFrom}, ${PALETTE.bgTo})`, color: PALETTE.cream }}
        data-testid="velocity-heartbeat-loading"
      >
        <Loader2 size={14} className="animate-spin" style={{ color: PALETTE.accent }} />
        <span className="text-xs uppercase tracking-[0.18em]">Tuning velocity signals…</span>
      </div>
    );
  }
  if (!data) return null;

  const deltaPct = data.inbound?.delta_pct ?? 0;
  const deltaIsPositive = deltaPct > 0;
  const deltaIsNegative = deltaPct < 0;
  const deltaColor = deltaIsPositive ? PALETTE.mint : deltaIsNegative ? PALETTE.accent : PALETTE.muted;
  const DeltaIcon = deltaIsPositive ? TrendingUp : deltaIsNegative ? TrendingDown : TrendingUp;

  return (
    <div
      className="relative rounded-2xl p-4 lg:p-5 overflow-hidden"
      style={{ background: `linear-gradient(95deg, ${PALETTE.bgFrom} 0%, ${PALETTE.bgTo} 100%)`, color: PALETTE.cream, border: '1px solid rgba(255,255,255,0.06)' }}
      data-testid="velocity-heartbeat"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3 lg:mb-4">
        <div className="flex items-center gap-2.5">
          <span
            className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-all duration-500 ${pulse ? 'scale-110' : 'scale-100'}`}
            style={{ background: PALETTE.accent + '22', boxShadow: pulse ? `0 0 0 6px ${PALETTE.accent}11` : 'none' }}
          >
            <Zap size={13} style={{ color: PALETTE.accent }} fill={PALETTE.accent} />
          </span>
          <div>
            <h3 className="text-xs font-black uppercase tracking-[0.18em]" style={{ color: PALETTE.cream }} data-testid="velocity-heartbeat-title">
              Velocity Heartbeat
            </h3>
            <p className="text-xs" style={{ color: PALETTE.muted }}>24h rolling · live</p>
          </div>
        </div>
        <span className="text-xs" style={{ color: PALETTE.muted }}>
          updates every 30s
        </span>
      </div>

      {/* Four-tile strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {/* Tile 1 — Inbound Enquiries */}
        <Tile
          icon={<TrendingUp size={13} />}
          iconColor={PALETTE.lavender}
          label="Inbound Enquiries"
          testId="vh-inbound"
        >
          <div className="flex items-baseline gap-2">
            <span className="text-2xl lg:text-3xl font-black tabular-nums" data-testid="vh-inbound-count">
              {data.inbound.count}
            </span>
            <span
              className="inline-flex items-center gap-0.5 text-xs font-bold tabular-nums"
              style={{ color: deltaColor }}
              data-testid="vh-inbound-delta"
            >
              <DeltaIcon size={10} />
              {deltaIsPositive && '+'}{deltaPct}%
            </span>
          </div>
          <p className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>vs prior 24h ({data.inbound.prev_count})</p>
        </Tile>

        {/* Tile 2 — Avg Response Time */}
        <Tile
          icon={<Clock size={13} />}
          iconColor={PALETTE.mint}
          label="Avg Response Time"
          testId="vh-response"
        >
          <div className="text-2xl lg:text-3xl font-black tabular-nums" data-testid="vh-response-label">
            {data.response.label}
          </div>
          <p className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>
            {data.response.sample_size > 0
              ? `median of ${data.response.sample_size} repl${data.response.sample_size === 1 ? 'y' : 'ies'}`
              : 'no replies in window'}
          </p>
        </Tile>

        {/* Tile 3 — Hot Handoff Rate */}
        <Tile
          icon={<Flame size={13} />}
          iconColor={PALETTE.accent}
          label="Hot Handoff Rate"
          testId="vh-hot"
        >
          <div className="flex items-baseline gap-2">
            <span className="text-2xl lg:text-3xl font-black tabular-nums" data-testid="vh-hot-rate">
              {data.hot_handoff.rate_pct}%
            </span>
          </div>
          <p className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>
            {data.hot_handoff.qualified_count} of {data.hot_handoff.sample_size} qualified
          </p>
        </Tile>

        {/* Tile 4 — Postmark Status */}
        <Tile
          icon={<ShieldCheck size={13} />}
          iconColor={data.postmark.status_pct === null ? PALETTE.muted : PALETTE.amber}
          label="Postmark Status"
          testId="vh-postmark"
        >
          <div className="flex items-baseline gap-2">
            <span className="text-2xl lg:text-3xl font-black tabular-nums" data-testid="vh-postmark-label">
              {data.postmark.label}
            </span>
          </div>
          <p className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>
            {data.postmark.sent_24h > 0
              ? `${data.postmark.sent_24h} send${data.postmark.sent_24h === 1 ? '' : 's'} last 24h`
              : 'awaiting first send'}
          </p>
        </Tile>
      </div>
    </div>
  );
};

const Tile = ({ icon, iconColor, label, children, testId }) => (
  <div
    className="rounded-xl px-3.5 py-3 lg:px-4 lg:py-3.5"
    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
    data-testid={testId}
  >
    <div className="flex items-center gap-1.5 mb-1.5">
      <span style={{ color: iconColor }}>{icon}</span>
      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'rgba(250,244,230,0.78)' }}>
        {label}
      </span>
    </div>
    {children}
  </div>
);

export default VelocityHeartbeat;
